// src/components/api/telegramBot.ts
export interface TelegramMessageData {
  nome: string;
  ipAddress: string;
  city: string;
  region: string;
  country: string;
  link: string;
  redirecionar: string;
  lang: string;
}

export class TelegramBotService {
  private static instance: TelegramBotService;
  private botToken: string;
  private chatIds: string[];

  private constructor() {
    this.botToken = process.env.REACT_APP_TELEGRAM_BOT_TOKEN || '';
    const chatIdsEnv = process.env.REACT_APP_TELEGRAM_CHAT_IDS || process.env.REACT_APP_TELEGRAM_CHAT_ID || '';
    
    this.chatIds = chatIdsEnv.split(',').map(id => id.trim()).filter(id => id);
    
    if (!this.botToken || this.chatIds.length === 0) {
      console.warn('Token do Telegram Bot ou Chat IDs não configurado');
    }
  }

  public static getInstance(): TelegramBotService {
    if (!TelegramBotService.instance) {
      TelegramBotService.instance = new TelegramBotService();
    }
    return TelegramBotService.instance;
  }

  private formatMessage(data: TelegramMessageData): string {
    const currentYear = new Date().getFullYear();
    const message = `
🔔 *Você recebeu uma nova submissão de Telegram - CineDeaf's*

👤 *Nome:* ${data.nome}
🌐 *IP:* ${data.ipAddress}
📍 *Localização:* ${data.city}, ${data.region} - ${data.country}
🔗 *Link de origem:* ${data.link}
⚡ *Redirecionar para:* ${data.redirecionar}
🌍 *Idioma:* ${data.lang}
📅 *Data/Hora:* ${new Date().toLocaleString('pt-BR')}

⋄     Copyright © ${currentYear}     ⋄
Todos os direitos reservados 🔖


    `.trim();

    return message;
  }

  public async sendMessage(data: TelegramMessageData): Promise<boolean> {
    if (!this.botToken || this.chatIds.length === 0) {
      throw new Error('Configuração do Telegram Bot incompleta');
    }

    const message = this.formatMessage(data);
    const promises = this.chatIds.map(chatId => this.sendMessageToChat(chatId, message));
    
    try {
      const results = await Promise.allSettled(promises);
      const failures = results.filter(result => result.status === 'rejected');
      
      if (failures.length > 0) {
        console.error(`Falha ao enviar para ${failures.length}/${this.chatIds.length} destinatários`);
        return failures.length < this.chatIds.length;
      }
      
      console.log(`Mensagem enviada com sucesso para ${this.chatIds.length} destinatários`);
      return true;
    } catch (error) {
      console.error('Erro ao enviar mensagens para o Telegram:', error);
      throw error;
    }
  }

  private async sendMessageToChat(chatId: string, message: string): Promise<void> {
    const response = await fetch(
      `https://api.telegram.org/bot${this.botToken}/sendMessage`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chat_id: chatId,
          text: message,
          parse_mode: 'Markdown',
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(`Erro na API do Telegram para chat ${chatId}:`, errorData);
      throw new Error(`Erro ao enviar mensagem para ${chatId}: ${response.status}`);
    }

    const result = await response.json();
    console.log(`Mensagem enviada com sucesso para chat ${chatId}:`, result);
  }

  public isConfigured(): boolean {
    return !!(this.botToken && this.chatIds.length > 0);
  }
}
